import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  CategoryScale,
  BarElement,
  LineElement,
  PointElement,
  LineController,
  BarController,
  Tooltip,
  Legend,
  Title,
  Filler,
} from "chart.js";
import "chartjs-adapter-moment";
import { Chart } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import { Select } from "antd";
import moment from "moment";
import chartAction from "../../../redux/transactions/actions";
import "./index.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  Tooltip,
  Legend,
  Title,
  Filler // For area fill under lines
);

const { Option } = Select;
const { getDailyChartData } = chartAction;

const ComboChart = () => {
  const dispatch = useDispatch();
  const { dailyChartData } = useSelector((state) => state.transactionReducer);
  const { agentId } = useSelector((state) => state.authReducer);

  const [selectedOption, setSelectedOption] = useState("daily");
  const [chartData, setChartData] = useState({ datasets: [] });

  useEffect(() => {
    if (!agentId) {
      dispatch(getDailyChartData());
    } else {
      dispatch(getDailyChartData(agentId));
    }
  }, [dispatch, agentId]);

  useEffect(() => {
    const rawData = dailyChartData?.data?.[selectedOption];
    if (!rawData) return;

    const currentHour = moment().hour();

    const filteredData =
      selectedOption === "daily"
        ? rawData.filter((entry) => {
            const [, endTime] = entry?.time?.split("-") || [];
            return moment(endTime, "HH:mm").hour() <= currentHour;
          })
        : rawData;

    const processedData = filteredData;

    const labels = processedData.map((entry) => {
      return selectedOption === "daily"
        ? moment(entry?.time?.split("-")[0], "HH:mm").toDate()
        : selectedOption === "fiveYears" || selectedOption === "allData"
        ? moment(entry?.time, "YYYY").toDate()
        : moment(entry?.time).toDate();
    });

    const successAmountData = processedData.map(
      (entry) => (entry?.data?.successAmount || 0) / 100
    );
    const failedAmountData = processedData.map(
      (entry) => (entry?.data?.failureAmount || 0) / 100
    );
    const successTransactions = processedData.map(
      (entry) => entry?.data?.successTransactions || 0
    );
    const failedTransactions = processedData.map(
      (entry) => entry?.data?.failedTransactions || 0
    );

    const { barPercentage, categoryPercentage } =
      selectedOption === "threeMonths"
        ? { barPercentage: 0.6, categoryPercentage: 0.6 }
        : { barPercentage: 0.9, categoryPercentage: 0.8 };

    setChartData({
      labels,
      datasets: [
        {
          type: "line",
          label: "Success Amount",
          data: labels.map((time, i) => ({ x: time, y: successAmountData[i] })),
          borderColor: "#36A2EB",
          backgroundColor: "rgba(54, 162, 235, 0.1)",
          borderWidth: 3,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#fff",
          pointBorderColor: "#36A2EB",
          pointBorderWidth: 2,
          tension: 0.3,
          yAxisID: "y1",
          fill: true,
        },
        {
          type: "line",
          label: "Failed Amount",
          data: labels.map((time, i) => ({ x: time, y: failedAmountData[i] })),
          borderColor: "#FF6384",
          backgroundColor: "rgba(255, 99, 132, 0.1)",
          borderWidth: 3,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#fff",
          pointBorderColor: "#FF6384",
          pointBorderWidth: 2,
          tension: 0.3,
          yAxisID: "y1",
          fill: true,
        },
        {
          type: "bar",
          label: "Success Transactions",
          data: labels.map((time, i) => ({
            x: time,
            y: successTransactions[i],
          })),
          backgroundColor: "rgba(75, 192, 192, 0.7)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
          borderRadius: 4,
          hoverBackgroundColor: "rgba(75, 192, 192, 1)",
          hoverBorderColor: "rgba(75, 192, 192, 1)",
          yAxisID: "y",
          barPercentage,
          categoryPercentage,
        },
        {
          type: "bar",
          label: "Failed Transactions",
          data: labels.map((time, i) => ({
            x: time,
            y: failedTransactions[i],
          })),
          backgroundColor: "rgba(255, 159, 64, 0.7)",
          borderColor: "rgba(255, 159, 64, 1)",
          borderWidth: 1,
          borderRadius: 4,
          hoverBackgroundColor: "rgba(255, 159, 64, 1)",
          hoverBorderColor: "rgba(255, 159, 64, 1)",
          yAxisID: "y",
          barPercentage,
          categoryPercentage,
        },
      ],
    });
  }, [dailyChartData, selectedOption]);

  const getTimeUnitForSelectedOption = (option) => {
    switch (option) {
      case "daily":
        return "hour";
      case "weekly":
        return "day";
      case "threeMonths":
      case "sixMonths":
      case "oneYear":
        return "month";
      case "fiveYears":
      case "allData":
        return "year";
      default:
        return "day";
    }
  };

  const formatTickLabel = (value) => {
    const date = moment(value);
    switch (selectedOption) {
      case "daily":
        return date.format("h A");
      case "weekly":
        return date.format("D MMM");
      case "threeMonths":
      case "sixMonths":
      case "oneYear":
        return date.format("MMM YYYY");
      case "fiveYears":
      case "allData":
        return date.format("YYYY");
      default:
        return date.format("D MMM");
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      },
    },
    plugins: {
      title: {
        display: true,
        text: "Transaction Analytics Dashboard",
        font: {
          size: 18,
          weight: "bold",
          family: "Arial",
        },
        color: "#333",
        padding: {
          top: 10,
          bottom: 30,
        },
      },
      legend: {
        position: "top",
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        onClick: (e, legendItem, legend) => {
          // Disable default click behavior to prevent hiding datasets
          return;
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 12,
        },
        padding: 10,
        cornerRadius: 5,
        displayColors: true,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              if (context.dataset.yAxisID === "y1") {
                label += "₹" + context.parsed.y.toLocaleString();
              } else {
                label += context.parsed.y.toLocaleString();
              }
            }
            return label;
          },
        },
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: getTimeUnitForSelectedOption(selectedOption),
          tooltipFormat:
            selectedOption === "daily"
              ? "h:mm A"
              : selectedOption === "weekly"
              ? "MMM D"
              : selectedOption === "threeMonths" ||
                selectedOption === "sixMonths" ||
                selectedOption === "oneYear"
              ? "MMM YYYY"
              : "YYYY",
        },
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#666",
          font: {
            size: 11,
            weight: "bold",
          },
          callback: (value) => formatTickLabel(value),
        },
      },
      y: {
        type: "time",
        time: {
          unit: getTimeUnitForSelectedOption(selectedOption),
          tooltipFormat:
            selectedOption === "daily"
              ? "h:mm A"
              : selectedOption === "weekly"
              ? "MMM D"
              : selectedOption === "threeMonths" ||
                selectedOption === "sixMonths" ||
                selectedOption === "oneYear"
              ? "MMM YYYY"
              : "YYYY",
        },
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#666",
          font: {
            size: 11,
            weight: "bold",
          },
          callback: (value) => formatTickLabel(value),
        },
      },
      y: {
        beginAtZero: true,
        position: "left",
        title: {
          display: true,
          text: "Transaction Count",
          color: "#666",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
        },
        ticks: {
          color: "#666",
          padding: 10,
          callback: function (value) {
            return value.toLocaleString();
          },
        },
      },
      y1: {
        beginAtZero: true,
        position: "right",
        title: {
          display: true,
          text: "Amount (₹)",
          color: "#666",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
        },
        ticks: {
          color: "#666",
          padding: 10,
          callback: function (value) {
            return "₹" + value.toLocaleString();
          },
        },
      },
    },
    animation: {
      duration: 1000,
      easing: "easeOutQuart",
    },
    elements: {
      bar: {
        hoverBackgroundColor: "rgba(0, 0, 0, 0.1)",
      },
    },
  };

  return (
    <div className="chart-container">
      <div className="chart-headerr">
        <h3>Transaction Analytics Dashboard</h3>
        <div className="chart-controls">
          <Select
            value={selectedOption}
            onChange={(val) => setSelectedOption(val)}
            style={{ width: 180 }}
            className="time-selector"
          >
            <Option value="allData">All Data</Option>
            <Option value="daily">Daily</Option>
            <Option value="oneWeek">One Week</Option>
            <Option value="oneMonth">One Month</Option>
            <Option value="threeMonths">3 Months</Option>
            <Option value="sixMonths">6 Months</Option>
            <Option value="oneYear">One Year</Option>
            <Option value="fiveYears">5 Years</Option>
          </Select>
        </div>
      </div>
      <div className="chart-wrapper">
        <Chart
          type="bar"
          data={chartData}
          options={chartOptions}
          height={400}
        />
      </div>
    </div>
  );
};

export default ComboChart;
